import React, { createContext, useState } from 'react';

export const LanguageContext = createContext();

const translations = {
    en: {
        skipToMain: "Skip to Main Content",
        screenReader: "Screen Reader Access",
        medProofTitle: "MedProof: Blockchain-Based Medicine Authenticity & Expiry Verification",
        atmanirbhar: "Ideas Powering Atmanirbhar Bharat",
        securedBy: "Secured by",
        batchVerification: "Public Scanner",
        manufacturerChain: "Manufacturer Node",
        networkStatus: "Network Status",
        support: "Support",
        // Landing Page
        heroTitle: "MedProof",
        heroSubtitle: "A decentralized national verification network enabling citizens, hospitals, and regulators to instantly confirm medicine authenticity, expiry, and recalls. Bridging the physical medicine box to an immutable digital record to safeguard India’s role as the \"Pharmacy of the World\"",
        initializing: "INITIALIZING...",
        publicScanner: "🔍 Public Scanner",
        manufacturerNode: "Manufacturer Node",
        problemTitle: "The Problem: \"Pharmacy of the World\" at Risk",
        problemDesc: "India is the global leader in generics, yet our reputation is threatened by a flood of counterfeit and expired medicines. Rural patients often cannot distinguish between a fake strip of antibiotics and a real one.",
        solutionTitle: "The Solution:",
        solutionDesc: "MedProof acts as a \"Digital Notary\", ensuring that if the blockchain says \"Expired\", the medicine is unsafe—regardless of the printed label.",
        systemArch: "⚙️ System Architecture",
        network: "Network",
        storage: "Storage",
        contract: "Contract",
        security: "Security",
        protocolWorkflow: "Protocol Workflow",
        sourceOfTruth: "Source of Truth",
        sourceDesc: "Manufacturers (e.g. Cipla, Sun Pharma) input Batch ID & CoA. A 'Batch Hash' is minted to the Registry.",
        immutableLink: "Immutable Link",
        immutableDesc: "Metadata includes \"Intended Distributor\" and IPFS Hash, creating a digital link fraudsters cannot guess.",
        trustCheck: "The \"Trust Check\"",
        trustDesc: "Patients scan the QR code. The Smart Contract instantly validates Expiry, Recalls, and Authenticity.",
        impactPillars: "Project Impact Pillars",
        nationalSecurity: "National Security",
        natSecDesc: "Reduces dependency on foreign centralized servers. Protects India's ₹3 Lakh Crore Pharma Industry from reputation damage.",
        economicTrust: "Economic Trust",
        ecoTrustDesc: "Restores faith in the \"Made in India\" label by eliminating the possibility of undetected counterfeits entering the supply chain.",
        ruralEmpowerment: "Rural Empowerment",
        ruralDesc: "Democratizes health safety. Allows anyone with a basic smartphone to verify medicine quality instantly.",
        // Patient Verify
        verifyTitle: " Public Authenticity Verification",
        verifySubtitle: "Verify medicine legitimacy via the secure blockchain ledger.",
        activeCamera: "Activate Camera Scanner",
        stopScanning: "Stop Scanning",
        alignQr: "Align QR Code within frame...",
        inputPlaceholder: "Input Batch Verification Code",
        querying: "Querying Ledger...",
        verifyAuth: "Verify Authenticity",
        verificationFailed: "❌ VERIFICATION FAILED: Batch ID not found in the Decentralized Registry.",
        criticalRecalled: "⛔ CRITICAL: BATCH RECALLED",
        recalledMsg: "DO NOT CONSUME. This batch has been flagged as UNSAFE by the manufacturer.",
        expiryAlert: "⚠️ ALERT: EXPIRY DATE EXCEEDED",
        expiryMsg: "This product is no longer safe for consumption.",
        verifiedAuthentic: "✅ VERIFIED AUTHENTIC",
        blockchainSecured: "✔ Blockchain Secured",
        manufacturerSigned: "✔ Manufacturer Signed",
        medName: "Medicine Name:",
        batchUid: "Batch UID:",
        expiryDate: "Expiry Date:",
        manufacturer: "Manufacturer:",
        logisticsPartner: "Logistics Partner:",
        qaCert: "📄 Quality Assurance Certificate (IPFS):",
        certMissing: "⚠️ Certificate Hash Missing",
        viewProof: "▶️ View Technical Proof (Hospital Mode)",
        hideProof: "🔽 Hide Technical Metadata",
        onChainProof: "⛓️ On-Chain Proof",
        ledgerStatus: "Ledger Status:",
        immutable: "Immutable / Finalized",
        signer: "Signer:",
        integrityCheck: "Integrity Check:",
        dataSource: "* Data retrieved directly from Polygon Network.",
        // Footer
        footerRights: "© 2026 MedProof: Blockchain-Based Medicine Authenticity & Expiry Verification.",
        teamRooted: "Team - Rooted",
        hackathonSub: "INNOVIT 2026 Hackathon Submission | Theme-3: MedTech | Atmanirbhar Bharat",
        // Support Page
        backToHome: "Back to Home",
        supportTitle: "Help & Support Center",
        contactUs: "Contact Us",
        metaMaskGuideTitle: "MetaMask Wallet Setup Guide (Easy Steps)",
        step1Title: "1. Download & Install",
        step1Desc: "Go to the App Store (iPhone) or Play Store (Android). Search for 'MetaMask' and install the app.",
        step2Title: "2. Create Wallet & Password",
        step2Desc: "Open the app and click 'Create a new wallet'. Set a strong password that you can remember.",
        step3Title: "3. Write Down Secret Words (Important!)",
        step3Desc: "You will see 12 Secret Words. Write them down on paper and keep them safe. Never share these words with anyone!",
        step4Title: "4. Connect to App",
        step4Desc: "Open this website. Click the 'Connect Wallet' button at the top. MetaMask will open/popup -> Click 'Approve' to connect.",
        faqTitle: "Frequently Asked Questions",
        faq1Q: "What if verification fails?",
        faq1A: "If verification fails, it means the batch ID was not found in the registry. The medicine might be counterfeit.",
        faq2Q: "How to report fake medicine?",
        faq2A: "Please contact us immediately via email with the batch details and images.",
        faq3Q: "Is this a government website?",
        faq3A: "No, this is a prototype developed for the INNOVIT 2026 Hackathon.",
    },
    hi: {
        skipToMain: "मुख्य सामग्री पर जाएं",
        screenReader: "स्क्रीन रीडर एक्सेस",
        medProofTitle: "मेडप्रूफ: ब्लॉकचेन-आधारित दवा प्रमाणिकता और समाप्ति सत्यापन",
        atmanirbhar: "आत्मनिर्भर भारत को सशक्त बनाने वाले विचार",
        securedBy: "द्वारा सुरक्षित",
        batchVerification: "सार्वजनिक स्कैन",
        manufacturerChain: "निर्माता नोड",
        networkStatus: "नेटवर्क स्थिति",
        support: "सहायता",
        // Landing Page
        heroTitle: "आत्मनिर्भर भारत को सशक्त बनाने वाले विचार",
        heroSubtitle: "एक विकेंद्रीकृत प्रमाणिकता प्रोटोकॉल। 'विश्व की फार्मेसी' की सुरक्षा के लिए भौतिक दवा बॉक्स को एक अपरिवर्तनीय डिजिटल रिकॉर्ड से जोड़ना।",
        initializing: "प्रारंभ हो रहा है...",
        publicScanner: "🔍 सार्वजनिक स्कैन",
        manufacturerNode: "निर्माता नोड",
        problemTitle: "समस्या: जोखिम में 'विश्व की फार्मेसी'",
        problemDesc: "भारत जेनरिक दवाओं में वैश्विक नेता है, फिर भी नकली और एक्सपायर्ड दवाओं की बाढ़ से हमारी प्रतिष्ठा खतरे में है। ग्रामीण मरीज अक्सर असली और नकली एंटीबायोटिक पट्टी के बीच अंतर नहीं कर पाते।",
        solutionTitle: "समाधान:",
        solutionDesc: "मेडप्रूफ 'डिजिटल नोटरी' के रूप में कार्य करता है, यह सुनिश्चित करता है कि यदि ब्लॉकचेन 'एक्सपायर्ड' कहता है, तो दवा असुरक्षित है—चाहे लेबल कुछ भी कहे।",
        systemArch: "⚙️ सिस्टम आर्किटेक्चर",
        network: "नेटवर्क",
        storage: "स्टोरेज",
        contract: "अनुबंध",
        security: "सुरक्षा",
        protocolWorkflow: "प्रोटोकॉल कार्यप्रवाह",
        sourceOfTruth: "सत्य का स्रोत",
        sourceDesc: "निर्माता (जैसे सिप्ला, सन फार्मा) बैच आईडी और सीओए इनपुट करते हैं। रजिस्ट्री में 'बैच हैश' मिंट किया जाता है।",
        immutableLink: "अपरिवर्तनीय लिंक",
        immutableDesc: "मेटाडेटा में 'लक्षित वितरक' और आईपीएफएस हैश शामिल हैं, एक ऐसा डिजिटल लिंक बनाना जिसका धोखेबाज अनुमान नहीं लगा सकते।",
        trustCheck: "'विश्वास जाँच'",
        trustDesc: "मरीज क्यूआर कोड स्कैन करते हैं। स्मार्ट कॉन्ट्रैक्ट तुरंत समाप्ति, रद्दीकरण और प्रमाणिकता को मान्य करता है।",
        impactPillars: "परियोजना प्रभाव के स्तंभ",
        nationalSecurity: "राष्ट्रीय सुरक्षा",
        natSecDesc: "विदेशी केंद्रीयकृत सर्वरों पर निर्भरता कम करता है। भारत के ₹3 लाख करोड़ के फार्मा उद्योग को प्रतिष्ठा की हानि से बचाता है।",
        economicTrust: "आर्थिक विश्वास",
        ecoTrustDesc: "आपूर्ति श्रृंखला में अनदेखी नकली दवाओं के प्रवेश की संभावना को समाप्त करके 'मेड इन इंडिया' लेबल पर विश्वास बहाल करता है।",
        ruralEmpowerment: "ग्रामीण सशक्तिकरण",
        ruralDesc: "स्वास्थ्य सुरक्षा का लोकतंत्रीकरण करता है। किसी को भी बेसिक स्मार्टफोन के साथ दवा की गुणवत्ता तुरंत सत्यापित करने की अनुमति देता है।",
         // Patient Verify
        verifyTitle: "🕵️‍♀️ सार्वजनिक प्रमाणिकता सत्यापन",
        verifySubtitle: "सुरक्षित ब्लॉकचेन लेज़र के माध्यम से दवा की वैधता सत्यापित करें।",
        activeCamera: "कैमरा स्कैनर सक्रिय करें",
        stopScanning: "स्कैनिंग रोकें",
        alignQr: "फ्रेम के भीतर क्यूआर कोड संरेखित करें...",
        inputPlaceholder: "बैच सत्यापन कोड दर्ज करें",
        querying: "लेज़र से पूछताछ कर रहा है...",
        verifyAuth: "प्रमाणिकता सत्यापित करें",
        verificationFailed: "❌ सत्यापन विफल: विकेंद्रीकृत रजिस्ट्री में बैच आईडी नहीं मिला।",
        criticalRecalled: "⛔ गंभीर: बैच वापस लिया गया",
        recalledMsg: "सेवन न करें। इस बैच को निर्माता द्वारा असुरक्षित घोषित किया गया है।",
        expiryAlert: "⚠️ चेतावनी: समाप्ति तिथि पार हो गई",
        expiryMsg: "यह उत्पाद अब सेवन के लिए सुरक्षित नहीं है।",
        verifiedAuthentic: "✅ सत्यापित असली",
        blockchainSecured: "✔ ब्लॉकचेन सुरक्षित",
        manufacturerSigned: "✔ निर्माता द्वारा हस्ताक्षरित",
        medName: "दवा का नाम:",
        batchUid: "बैच यूआईडी:",
        expiryDate: "समाप्ति तिथि:",
        manufacturer: "निर्माता:",
        logisticsPartner: "लॉजिस्टिक्स पार्टनर:",
        qaCert: "📄 गुणवत्ता आश्वासन प्रमाण पत्र (IPFS):",
        certMissing: "⚠️ प्रमाणपत्र हैश गायब",
        viewProof: "▶️ तकनीकी प्रमाण देखें (अस्पताल मोड)",
        hideProof: "🔽 तकनीकी मेटाडेटा छिपाएं",
        onChainProof: "⛓️ ऑन-चेन प्रमाण",
        ledgerStatus: "लेज़र स्थिति:",
        immutable: "अपरिवर्तनीय / अंतिम",
        signer: "हस्ताक्षरकर्ता:",
        integrityCheck: "अखंडता जाँच:",
        dataSource: "* डेटा सीधे पॉलीगॉन नेटवर्क से प्राप्त किया गया।",
        // Footer
        footerRights: "© 2026 मेडप्रूफ: ब्लॉकचेन-आधारित दवा प्रमाणिकता और समाप्ति सत्यापन।",
        teamRooted: "टीम - रूटेड",
        hackathonSub: "इनोविट 2026 हैकाथॉन सबमिशन | थीम-3: मेडटेक | आत्मनिर्भर भारत",
        // Support Page
        backToHome: "वापस जाएं",
        supportTitle: "सहायता और समर्थन केंद्र",
        contactUs: "संपर्क करें",
        metaMaskGuideTitle: "मेटामास्क वॉलेट सेटअप गाइड (आसान चरण)",
        step1Title: "1. डाउनलोड और इंस्टॉल करें",
        step1Desc: "अपने फोन पर App Store या Play Store पर जाएं। 'MetaMask' खोजें और ऐप इंस्टॉल करें।",
        step2Title: "2. वॉलेट बनाएं और पासवर्ड सेट करें",
        step2Desc: "ऐप खोलें और 'Create a new wallet' पर क्लिक करें। एक मजबूत पासवर्ड सेट करें जो आपको याद रहे।",
        step3Title: "3. गुप्त शब्द लिख लें (महत्वपूर्ण!)",
        step3Desc: "आपको 12 गुप्त शब्द (Secret Words) दिखाई देंगे। उन्हें कागज पर लिख लें और सुरक्षित जगह पर रखें। ये शब्द किसी को न बताएं!",
        step4Title: "4. ऐप से कनेक्ट करें",
        step4Desc: "इस वेबसाइट को खोलें। ऊपर 'Connect Wallet' बटन दबाएं। मेटामास्क खुलेगा -> 'Approve' या 'Connect' पर क्लिक करें।",
        faqTitle: "अक्सर पूछे जाने वाले प्रश्न",
        faq1Q: "यदि सत्यापन विफल हो जाता है तो क्या होगा?",
        faq1A: "यदि सत्यापन विफल हो जाता है, तो इसका मतलब है कि रजिस्ट्री में बैच आईडी नहीं मिला। दवा नकली हो सकती है।",
        faq2Q: "नकली दवा की रिपोर्ट कैसे करें?",
        faq2A: "कृपया बैच विवरण और छवियों के साथ ईमेल के माध्यम से तुरंत हमसे संपर्क करें।",
        faq3Q: "क्या यह एक सरकारी वेबसाइट है?",
        faq3A: "नहीं, यह इनोविट 2026 हैकाथॉन के लिए विकसित एक प्रोटोटाइप है।",
    }
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState('en');

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'hi' : 'en');
    };

    const t = (key) => {
        return translations[language][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};
